'use client';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, ControlledInput, Snackbar, Typography } from 'components';
import { handlerEnter } from 'handlers/handlerEnter';
import { onErrorHelper } from 'handlers/handlerMutations';
import { toaster } from 'helpers/toaster';
import useAuth from 'hooks/useAuth';
import { EditIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useClientAccountUpdate } from 'providers/UserProvider/useClientAccountUpdate';
import { useCurrentUser } from 'providers/UserProvider/useCurrentUser';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { notifyException } from 'services/appsignal';
import { ESpansException } from 'types/ESpans';
import { TErrorAws } from 'types/TErrorAws';
import { TScheme } from 'types/TScheme';
import { isErrorAws } from 'utils/isErrorAws';
import * as yup from 'yup';

type NameChange = {
  firstName: string;
  lastName: string;
  email: string;
};

const ClientPage = () => {
  const t = useTranslations();

  const [isEditing, setIsEditing] = useState(false);
  const auth = useAuth();
  const router = useRouter();
  const { data: currentUser } = useCurrentUser();

  const { data, refetch, error, isError, isFetched } = useCurrentUser();

  const { mutateAsync } = useClientAccountUpdate();

  const schema = yup.object().shape({
    firstName: yup.string().required(t('messages.fields.first_name_required')),
    lastName: yup.string().required(t('messages.fields.last_name_required')),
    email: yup
      .string()
      .nullable()
      .email(t('messages.fields.email_validation'))
      .required(t('messages.fields.email_required'))
  } as TScheme<NameChange>);

  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    reset
  } = useForm<NameChange>({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: ''
    }
  });

  const handleCancel = () => setIsEditing(false);
  const handleEdit = () => setIsEditing(true);

  const onChangeEmail = async (email: string) => {
    try {
      const searchParams = new URLSearchParams();
      searchParams.set('email', email);
      searchParams.set('newEmailChange', 'true');
      const user = await auth.currentAuthenticatedUser();
      const result = await auth.updateUserAttributes(user, {
        email
      });

      if (result === 'SUCCESS') {
        toaster.success(t('messages.success.change_email'));
        handleCancel();
        return router.push('/auth/verification?' + searchParams.toString());
      }
    } catch (error) {
      notifyException(error, ESpansException.AccountDetailsException);

      handleEdit();
      if (isErrorAws(error))
        return toaster.error(t(`messages.error.aws.${(error as TErrorAws)?.code}` as any));

      return onErrorHelper(t('messages.error.changing_email'));
    }
  };

  const handlerUpdateUserName = async () => {
    if (data?.emailPreferences) {
      try {
        const dataValues = getValues();
        await mutateAsync({
          firstName: dataValues.firstName,
          lastName: dataValues.lastName,
          locale: String(data?.locale),
          timeZone: String(data?.timeZone),
          emailNotifications: data?.emailPreferences
        });
        refetch();

        if (dataValues.email !== currentUser?.email) return onChangeEmail(dataValues.email);

        toaster.success(t('messages.success.personal_info'));
        handleCancel();
      } catch (error) {
        handleEdit();
        notifyException(error, ESpansException.AccountDetailsException);
        return onErrorHelper(t('messages.error.personal_info'));
      }
    }
  };

  useEffect(() => {
    if (isFetched)
      reset({
        firstName: data?.firstName || '',
        lastName: data?.lastName || '',
        email: currentUser?.email || ''
      });
  }, [data?.firstName, data?.lastName, currentUser?.email, isFetched, reset]);

  const onSubmit = handleSubmit(handlerUpdateUserName);

  return (
    <div>
      <div>
        <div className="flex justify-between">
          <Typography variant="regular1" className="text-tertiary-100">
            {t('account_details.general.personal_info')}
          </Typography>
          {!isEditing && (
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              className="!px-3"
              startAdornment={<EditIcon />}
              key={'account_details.edit'}
              label={t('glossary.new.card.edit')}
              onClick={handleEdit}
            />
          )}
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex gap-4">
            <ControlledInput
              control={control}
              error={errors.firstName}
              label={t('labels.first_name')}
              type="text"
              name="firstName"
              classNameParent="w-full"
              placeholder={t('placeholders.first_name')}
              disabled={!isEditing}
            />
            <ControlledInput
              control={control}
              error={errors.lastName}
              label={t('labels.last_name')}
              type="text"
              name="lastName"
              classNameParent="w-full"
              placeholder={t('placeholders.last_name')}
              disabled={!isEditing}
            />
          </div>
          <ControlledInput
            control={control}
            error={errors.email}
            placeholder={t('placeholders.email')}
            label={t('labels.email')}
            type="email"
            name="email"
            className="select-none"
            disabled={!isEditing}
          />
        </div>
        {isError && (
          <Snackbar variant="danger" description={error && (error as unknown as [])?.join('. ')} />
        )}
        {isEditing && (
          <div className="mt-4 flex justify-end">
            <Button
              label={t('account_details.cancel')}
              onClick={handleCancel}
              size="medium"
              className="flex items-center"
              variant="text"
            />
            <Button
              label={t('account_details.general.save_changes')}
              onClick={onSubmit}
              onKeyDown={handlerEnter(onSubmit)}
              size="medium"
              disabled={!data?.emailPreferences}
              className="flex items-center"
              variant="contained"
            />
          </div>
        )}
      </div>
    </div>
  );
};
export default ClientPage;
